import { useCallback, useState } from "react"
import { serialize } from "object-to-formdata"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { routes } from "router/routes"
import { updateTrip } from "store/trips"
import { notification } from "ui/Notification"
import { loadingSelector } from "modules/loader-watchers/selectors"

export const useListingsModals = ({ setSelectedType, onActionComplete }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [isAddNewListingModalOpen, setIsAddNewListingModalOpen] = useState(false)
  const [editCategoryModal, setEditCategoryModal] = useState({ visible: false, trip: null })
  const [isManageCategoriesOpen, setIsManageCategoriesOpen] = useState(false)
  const openManageCategories = useCallback(() => setIsManageCategoriesOpen(true), [])
  const closeManageCategories = useCallback(() => setIsManageCategoriesOpen(false), [])
  // Trip-update loading flag from redux (set by updateTrip via requestedUpdateTrip).
  // Shared across trips.trip updates, but the category modal is only open during a
  // category save, so it reflects exactly that here.
  const isSavingCategory = useSelector(loadingSelector("trips.trip"))

  const openAddNewListing = useCallback(() => setIsAddNewListingModalOpen(true), [])

  const closeAddNewListing = useCallback(() => {
    setIsAddNewListingModalOpen(false)
    setSelectedType("private")
  }, [setSelectedType])

  const continueAddNewListing = useCallback(
    (type) => {
      if (!type) return
      setIsAddNewListingModalOpen(false)
      navigate(`${routes.proNewExperienceV2Path({ id: "new" })}?type=${type}`)
    },
    [navigate]
  )

  const addFromEmptyState = useCallback(
    (type) => {
      if (type) return continueAddNewListing(type)
      openAddNewListing()
      return Promise.resolve()
    },
    [continueAddNewListing, openAddNewListing]
  )

  const openEditCategory = useCallback((trip) => setEditCategoryModal({ visible: true, trip }), [])
  const closeEditCategory = useCallback(() => setEditCategoryModal({ visible: false, trip: null }), [])

  // Persists the category and sub categories via the shared trip update API, with the same
  // fields and encoding as the wizard's Details step (category_id + subcategory_ids).
  const saveCategory = useCallback(
    async ({ categoryId, subcategoryIds }) => {
      const tripId = editCategoryModal.trip?.id
      if (!tripId || categoryId == null || !subcategoryIds?.length) return
      try {
        await dispatch(
          updateTrip(tripId, serialize({ trip: { category_id: categoryId, subcategory_ids: subcategoryIds } }, { allowEmptyArrays: true }))
        )
        notification.success({
          message: t("experiences.edit_category.success", { defaultValue: "Category updated" }),
          type: "success",
          closable: true,
          duration: 3000
        })
        closeEditCategory()
        onActionComplete?.()
      } catch (error) {
        console.error("saveCategory error:", error)
        notification.error({
          message: t("experiences.edit_category.error", { defaultValue: "Failed to update category" }),
          type: "error",
          closable: true,
          duration: 5000
        })
      }
    },
    [editCategoryModal.trip?.id, dispatch, t, closeEditCategory, onActionComplete]
  )

  return {
    isAddNewListingModalOpen,
    openAddNewListing,
    closeAddNewListing,
    continueAddNewListing,
    addFromEmptyState,
    editCategoryModal,
    openEditCategory,
    closeEditCategory,
    saveCategory,
    isSavingCategory,
    isManageCategoriesOpen,
    openManageCategories,
    closeManageCategories
  }
}
